import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  Watch,
  State,
  Listen,
  Fragment
} from '@stencil/core';

import * as d3 from 'd3';
import { select } from 'd3-selection';
import {
  CHART_TYPE,
  DATA_TYPE,
  LINE_STYLE,
  DATA_VISUALIZATION_HUE,
  DATA_VISUALIZATION_HUE_VARIANT,
  BAR_CHART_INVALID_CONFIG
} from '../../constants';
import { getChartModel } from '../../utils/model-helper';
import {
  IBarChart,
  SingleBarChart,
  GroupedBarChart,
  IChartData
} from '../../models';

@Component({
  tag: 'uui-bar-chart',
  styleUrl: 'uui-bar-chart.scss',
  shadow: true
})
export class UuiBarChart {
  @Element() element: HTMLElement;

  @Prop() config: IBarChart;
  @State() width: number;
  @State() highlightedGroup: string = '';

  @Event({ bubbles: true, composed: true }) legendClick: EventEmitter;

  //Todo: Find approprate d3 data types
  public chart: any;
  public barsSvgElement: any;

  noBenchmarkToolTipElement: HTMLElement;
  model: SingleBarChart | GroupedBarChart;
  plotAreaWidth: number;
  toolTipElement: HTMLElement;
  highlighedBarData: IChartData;
  hasEmptyMessageSlot: boolean;

  @Listen('resize', { target: 'window' })
  windowResize() {
    if (this.model) {
      this.createChart();
    }
  }

  @Watch('config')
  onConfigChanged(): void {
    this.buildModel();
    this.createChart();
  }

  componentWillLoad(): void {
    this.hasEmptyMessageSlot = !!this.element.querySelector(
      '[slot="empty-message"]'
    );
    this.buildModel();
  }

  componentDidLoad(): void {
    this.createChart();
  }

  buildModel(): void {
    // To handle empty or null config
    if (!this.config || Object.keys(this.config).length == 0) {
      this.config = BAR_CHART_INVALID_CONFIG as IBarChart;
    }

    this.model = getChartModel<
      CHART_TYPE.SINGLE_BAR | CHART_TYPE.GROUPED_BAR >(this.config as IBarChart);
  }

  createChart(): void {
    if (!this.model) {
      return;
    }

    if (!this.model.series?.length) {
      return;
    }

    this.width = this.model.stretchToMaxWidth
      ? this.element?.getBoundingClientRect().width
      : this.model.width;

    const svgElement = select(this.element.shadowRoot.querySelector('svg'))
      .attr('width', this.width)
      .attr('height', this.model.height);

    this.plotAreaWidth = this.model.stretchToMaxWidth
      ? this.width * this.model.widthModifier
      : this.model.plotAreaWidth;

    this.model.plotAreaWidth = this.plotAreaWidth;

    svgElement.selectAll('*').remove();

    this.chart = svgElement
      .append('g')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    // Y Axis Title
    if (this.model.yAxis.showAxisLabel) {
      this.chart
        .append('text')
        .attr(
          'transform',
          `translate(${this.model.yAxisTitleLeftMargin} ${
            this.model.plotAreaHeight / 2
          }) rotate(-90)`
        )
        .attr('text-anchor', 'middle')
        .text(this.model.yAxis.title)
        .attr('class', 'uui-dv-axis-label');
    }

    // X Axis Title
    const xAxisTitle = this.chart
      .append('text')
      .attr('x', 0)
      .attr('y', this.model.plotAreaHeight + 45) // this calculation should be less than the overall chart height
      .attr('text-anchor', 'start')
      .text(this.model.xAxis.title)
      .attr('class', 'uui-dv-axis-label');

    const yAxisSvg = svgElement
      .append('g')
      .attr('class', 'chart__axis--hidden uui-dv-data-label')
      .attr(
        'transform',
        `translate(${this.model.yAxisSvgLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    const xAxisSvg = svgElement
      .append('g')
      .attr('class', 'chart__axis uui-dv-data-label')
      .attr(
        'transform',
        `translate(${
          this.model.plotAreaLeftMargin +
          (this.model.type === CHART_TYPE.GROUPED_BAR && !this.model.isHorizontal
            ? this.model.barWidth / 2
            : 0)
        }, ${this.model.plotAreaTopMargin + this.model.plotAreaHeight})`
      );

    if (!this.model.xAxis.showAxisLine) {
      xAxisSvg.attr('class', 'chart__axis--hidden uui-dv-axis-label');
    }

    if (this.model.isSmallChart) {
      yAxisSvg.call(this.model.leftAxis.tickValues([]));
    }

    yAxisSvg.call(this.model.leftAxis);
    xAxisSvg.call(this.model.bottomAxis);
    if (!this.model.isHorizontal) {
      xAxisSvg
        .selectAll('g.tick')
        .attr('text-anchor', 'end')
        .attr('transform', (d: any) => {
          const xPosition = this.model.xScale(d) + this.model.barWidth / 2;
          return `translate(${xPosition}, 0)`;
        });

      xAxisSvg.selectAll('g.tick text').attr('dy', () => {
        return '0.95em';
      });
    }

    const xAxisWidth =
      (xAxisSvg.node() as SVGElement)?.getBoundingClientRect().width * 0.98;
    const xAxisTitleWidth = xAxisTitle.node()?.getComputedTextLength();

    if (xAxisTitleWidth > xAxisWidth) {
      xAxisTitle
        .attr('x', xAxisTitleWidth / 2)
        .style('max-width', xAxisWidth + 'px')
        .call(this.wrapAndPositionText, xAxisWidth, xAxisWidth / 2);

      this.model.noOfAxisLines = xAxisTitle.selectAll('tspan').size();
      this.model.height = this.model.getCalculatedHeight();
      svgElement.attr('width', this.width).attr('height', this.model.height);
    } else {
      xAxisTitle
        .attr('x', this.plotAreaWidth / 2)
        .attr('text-anchor', 'middle');
    }

    if (this.model.isHorizontal && this.model.type != CHART_TYPE.GROUPED_BAR) {
      yAxisSvg
        .call(this.model.leftAxis)
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr(
          'transform',
          `translate(80, ${-(this.model.yScale.bandwidth() / 3)})`
        );
    }

    if (this.model.isSmallChart && !this.model.isHorizontal) {
      xAxisSvg
        .call(this.model.bottomAxis)
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('transform', `translate(10, 0)`);
    }

    this.barsSvgElement = svgElement
      .append('g')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    this.updateChart();
  }

  public wrapAndPositionText(text, width, xCoordinate) {
    text.each(function () {
      const text = d3.select(this);
      const words = text.text().split(/\s+/).reverse();
      let word;
      let line = [];
      const lineHeight = 20;
      let tspan = text.text(null).append('tspan').attr('x', xCoordinate);

      while ((word = words.pop())) {
        line.push(word);
        tspan.text(line.join(' '));
        if (tspan.node()?.getComputedTextLength() > width) {
          line.pop();
          tspan.text(line.join(' '));
          line = [word];
          tspan = text
            .append('tspan')
            .attr('x', xCoordinate)
            .attr('dy', lineHeight)
            .text(word);
        }
      }
    });
  }

  public updateChart(): void {
    this.model.series.forEach((series, seriesIndex) => {
      const bars = this.barsSvgElement
        .selectAll('.bar')
        .data(series.data.filter(bar => !bar.isHidden));
      const smallChartDataLabels = this.barsSvgElement
        .selectAll('.bar')
        .data(series.data);

      bars.exit().remove();

      // bars
      const paths = bars
        .enter()
        .append('rect')
        .style('fill', (d: any) => 
           this.model.getColorForDataNIndex(d, seriesIndex)
        )
        .attr('x', (d: any) =>
          this.model.getBarOriginCoordinateX(this.model.xScale, d, seriesIndex)
        )
        .attr('y', (d: any) =>
          this.model.getBarOriginCoordinateY(this.model.yScale, d, seriesIndex)
        )
        .attr('width', (d: any) => this.model.getBarWidth(this.model.xScale, d))
        .attr('height', (d: any) =>
          this.model.getBarHeight(this.model.yScale, d)
        );

      if (this.model.tooltip) {
        paths
          .on('mouseover', (e: MouseEvent, d: any) => {
            // Dim all bars/groups except the hovered one
            bars
              .enter()
              .selectAll('rect')
              .filter(group => group?.name !== d.name)
              .attr('opacity', '30%');

            this.highlightedGroup = d.name;
            this.highlighedBarData = d;
            this.toogleTooltip(true);
            this.updateTooltipPosition(e.x, e.y);
          })
          .on('mousemove', (e: MouseEvent) => {
            this.updateTooltipPosition(e.x, e.y);
          })
          .on('mouseout', () => {
            bars.enter().selectAll('rect').attr('opacity', '100%');

            this.highlightedGroup = '';
            this.toogleTooltip(false);
            this.updateTooltipPosition(-1000, -1000); // to displace the tooltip
          });
      }

      // data label
      if(!this.config.hideDataLabels) {
        bars
        .enter()
        .append('text')
        .attr('text-anchor', 'middle')
        .attr(
          'class',
          `uui-dv-data-label ${
            this.model.isHorizontal ? 'chart-text-label-start' : ''
          }`
        )
        .attr('x', (d: any) =>
          this.model.getDataLabelCoordinateX(this.model.xScale, d, seriesIndex)
        )
        .attr(
          'y',
          (d: any) =>
            this.model.getDataLabelCoordinateY(
              this.model.yScale,
              d,
              seriesIndex
            ) -
            this.model.barHeight * 0.4
        )
        .text((d: any) => {
          return `${
            this.model.valuePrefix || ''
          }${this.model.getFormattedDataLabel(d.y, d.displayLabelY)}${
            this.model.valueSuffix || ''
          }`;
        });
      }
      

      // average line
      if (this.model.isHorizontal && this.model.average.length > 0) {
        this.displayAverageLinesAndRange();
      }

      if (
        this.model.isSmallChart &&
        this.model.isHorizontal &&
        seriesIndex === 0
      ) {
        const path = smallChartDataLabels
          .enter()
          .append('svg:foreignObject')
          .attr('x', 0)
          .attr(
            'y',
            (d: any) =>
              this.model.getBarOriginCoordinateY(
                this.model.yScale,
                d,
                seriesIndex
              ) - (!d.isHidden ? 20 : 10)
          )
          .attr('width', this.width * 0.9)
          .attr('height', 20);

        var div = path.append('xhtml:div').attr('class', 'uui-dv-legend-label');

        this.getBarDataLabelHtml(div);

        if (this.model.legend && this.model.legend.callbackPayload) {
          path.on('click', (e: any, d: any) => {
            this.emitLabelPayload(e, series.data.indexOf(d));
          });
        }
      }
    });
  }

  getBarDataLabelHtml(div: any): void {
    // data point label
    div.append('span').html((d: any) => d.name);

    // No Benchmark Icon and tooltip
    div
      .append('span')
      .on('mouseover', (e: MouseEvent, d: any) => {
        if (this.model.displayNoBenchmarkIcon(d.name)) {
          this.toggleNoBenchmarkTooltip(true);
          this.updateNoBenchMarkTooltipPosition(e.x, e.y);
        }
      })
      .on('mousemove', (e: MouseEvent, d: any) => {
        if (this.model.displayNoBenchmarkIcon(d.name)) {
          this.updateNoBenchMarkTooltipPosition(e.x, e.y);
        }
      })
      .on('mouseout', () => {
        this.toggleNoBenchmarkTooltip(false);
        this.updateNoBenchMarkTooltipPosition(-1000, -1000); // to displace the tooltip
      })
      .html((d: any) => {
        const displayNoBenchmarkIcon =
          this.model.type == CHART_TYPE.GROUPED_BAR &&
          this.model.displayNoBenchmarkIcon(d.name);
        return displayNoBenchmarkIcon
          ? ` <uui-icon type="info-circle" color="#b37400" size="sm"</uui-icon>AB`
          : '';
      });

    // data label hyperlink
    div.append('span').html((_d: any, index: any) => {
      const isLinkEnabled =
        this.model.legend?.callbackPayload?.length > index &&
        this.model.legend.callbackPayload[index].enabled;
      return isLinkEnabled
        ? `<span class="chart-link">${this.model.legend.callbackPayload[index].label}</span>`
        : '';
    });
  }

  displayAverageLinesAndRange(): void {
    const averageRangeStart = this.model.average.find(
      a => a.type == DATA_TYPE.RANGE_START
    );
    const averageRangeEnd = this.model.average.find(
      a => a.type == DATA_TYPE.RANGE_END
    );

    const solidLine = this.model.average.find(
      a => a.lineStyle == LINE_STYLE.SOLID
    );

    var solidLineColor =
      solidLine?.color ||
      DATA_VISUALIZATION_HUE[DATA_VISUALIZATION_HUE_VARIANT.MAGENTA];

    var dashedLineColor =
      averageRangeStart?.color ||
      averageRangeEnd?.color ||
      DATA_VISUALIZATION_HUE[DATA_VISUALIZATION_HUE_VARIANT.ORANGE];

    // Range Transparent Rectangle
    if (averageRangeStart && averageRangeEnd) {
      this.barsSvgElement
        .append('rect')
        .attr('x', this.model.xScale(averageRangeStart.y))
        .attr('y', -20) // 20 offset for triangle pointers at line ends
        .attr('stroke', dashedLineColor)
        .attr(
          'width',
          this.model.xScale(averageRangeEnd.y) -
            this.model.xScale(averageRangeStart.y)
        )
        .attr('height', this.model.plotAreaHeight + 20)
        .style('opacity', 0.05);
    }

    // Average Lines
    this.model.average
      .sort((a, b) => +b.lineStyle - +a.lineStyle)
      .forEach(average => {
        this.barsSvgElement
          .append('line')
          .attr('class', 'chart__average-line')
          .attr(
            'stroke-dasharray',
            average.lineStyle == LINE_STYLE.SOLID ? 'none' : '10 5'
          )
          .attr(
            'stroke',
            average.lineStyle == LINE_STYLE.SOLID
              ? solidLineColor
              : dashedLineColor
          )
          .attr('x1', this.model.xScale(average.y))
          .attr('y1', -10)
          .attr('x2', this.model.xScale(average.y))
          .attr('y2', this.model.plotAreaHeight);

        if (average.lineStyle == LINE_STYLE.SOLID) {
          this.barsSvgElement
            .append('rect')
            .attr('class', 'chart__average-line')
            .attr('stroke', solidLineColor)
            .attr('fill', solidLineColor)
            .attr('x', this.model.xScale(average.y) - 5)
            .attr('y', -20)
            .attr('width', 10)
            .attr('height', 10)
            .attr('rx', 2);
        }

        // triangle pointer at line end
        if (average.lineStyle == LINE_STYLE.DOTTED) {
          this.barsSvgElement
            .append('polygon')
            .attr('class', 'chart__average-line')
            .attr('stroke', dashedLineColor)
            .attr('fill', dashedLineColor)
            .attr(
              'points',
              `${this.model.xScale(average.y) + 5}, 0  
          ${this.model.xScale(average.y)}, -8 
          ${this.model.xScale(average.y) - 5}, 0
          `
            );
        }
      });
  }

  emitLabelPayload(event: Event, dataIndex: number): void {
    event.preventDefault();
    event.stopPropagation();

    const payload =
      this.model.legend.callbackPayload.length > dataIndex
        ? this.model.legend.callbackPayload[dataIndex]
        : undefined;

    this.legendClick.emit(payload);
  }

  toggleNoBenchmarkTooltip(isShow: boolean): void {
    this.noBenchmarkToolTipElement.style.opacity = isShow ? '1' : '0';
  }

  getHorizontalSmallChartLabelHtml(labelName, index): string {
    const isLinkEnabled =
      this.model.legend?.callbackPayload?.length > index &&
      this.model.legend.callbackPayload[index].enabled;

    return `<div class="uui-dv-legend-label">
              <span>${labelName}</span>
              ${
                isLinkEnabled
                  ? `<span class="chart-link">${this.model.legend.callbackPayload[index].label}</span>`
                  : ''
              }
            </div>`;
  }

  getLegendItem(legendName: string, legendIndex: number): JSX.Element {
    return (
      <Fragment>
        <div class="chart__legend--flex-row">
          {/* -------- Legend Color Palete -------------- */}
          <div
            class="chart__legend--dot uui-dv-legend-label"
            style={{
              backgroundColor:
                   this.model.getColorForDataNIndex(
                      this.model.series[0].data[0],
                      legendIndex)
            }}
          ></div>

          {/* -------- Legend Label ------------ */}
          <div class="chart__legend-label uui-dv-legend-label">
            {legendName}
          </div>
        </div>
      </Fragment>
    );
  }

  getLegendItems(legendItems: JSX.Element | JSX.Element[]): JSX.Element {
    return (
      <div 
        class="chart__legend--flex-centre" 
        style={{ 
          width: `${this.plotAreaWidth}px`,
          transform: 
            `translate(${this.model.plotAreaLeftMargin}px)`
        }}
      >{legendItems}
      </div>
    );
  }

  getChartLegend(): JSX.Element {
    const legendItems = this.model.computedLengedLabels.map(
      (legendName, legendIndex) => {
        return this.getLegendItem(legendName, legendIndex);
      }
    );

    return <div> {this.getLegendItems(legendItems)} </div>;
  }

  updateNoBenchMarkTooltipPosition(x: number, y: number): void {
    const tooltipHeight =
      this.noBenchmarkToolTipElement?.getBoundingClientRect()?.height + 20;

    this.noBenchmarkToolTipElement.style.left = `${x - 15}px`;
    this.noBenchmarkToolTipElement.style.top = `${y - tooltipHeight}px`;
  }

  getNoBenchmarkTooltip(): JSX.Element {
    return (
      <div>
        <slot name="no-benchmark-tooltip" />
      </div>
    );
  }

  toogleTooltip(isShow: boolean): void {
    this.toolTipElement.style.opacity = isShow ? '1' : '0';
  }

  updateTooltipPosition(x: number, y: number): void {
    const documentWidth = document.body.offsetWidth;
    const rightSpace = documentWidth - x;
    const tooltipWidth =
      this.toolTipElement?.getBoundingClientRect()?.width + 20;

    if (rightSpace <= tooltipWidth) {
      this.toolTipElement.classList.remove(
        'chart__tooltip--container-pointer-left'
      );
      this.toolTipElement.classList.add(
        'chart__tooltip--container-pointer-right'
      );
      this.toolTipElement.style.left = `${x - tooltipWidth}px`;
    } else {
      this.toolTipElement.classList.remove(
        'chart__tooltip--container-pointer-right'
      );
      this.toolTipElement.classList.add(
        'chart__tooltip--container-pointer-left'
      );
      this.toolTipElement.style.left = `${x + 20}px`;
    }
    this.toolTipElement.style.top = `${y - 12}px`;
  }

  getTooltipRow(bar: any, data: IChartData, barIndex: number): JSX.Element {
    data.color = bar.color;
    return (
      <div class="chart__legend--flex-row uui-dv-legend-label">
        <div
          class="chart__legend--dot uui-dv-legend-label"
          style={{
            backgroundColor: this.model.getColorForDataNIndex(data, barIndex)
          }}
        ></div>
        <div>{bar.barName}</div>
        <div class="chart__tooltip--value">
          {bar.displayLabelY || bar.barValue}
        </div>
        {this.model.tooltip.showBenchmarkDiff && (
          <div class="chart__tooltip--value">{bar.differenceWithBenchmark}</div>
        )}
      </div>
    );
  }

  getTooltip(data: IChartData, highlightedGroup: string): JSX.Element {
    highlightedGroup = highlightedGroup || data.name;

    const selectedGroupTooltipData = this.model.groupedBarTooltipData.find(
      g => g.name == highlightedGroup
    );

    return (
      <div class="chart__legend--flex-column">
        {this.model.tooltip.showTitle && (
          <div class="uui-dv-legend-title">{this.highlighedBarData.name}</div>
        )}

        <div class="chart__legend--row-container">
          {selectedGroupTooltipData.tooltipData
            .filter(f => !f.isHidden)
            .map((bar, barIndex) => this.getTooltipRow(bar, data, barIndex))}
        </div>
      </div>
    );
  }

  //#region - Tooltip
  getChartTooltip(): JSX.Element {
    const rows = this.getTooltip(this.highlighedBarData, this.highlightedGroup);

    return (
      <Fragment>
        <div class="chart__legend--flex-centre">{rows}</div>
      </Fragment>
    );
  }
  //#endregion
  render() {
    const chartSectionClass = this.model.isShowChart
      ? 'chart__wrapper'
      : 'chart--hidden';

    const viewBoxCoords =
      this.model.stretchToMaxWidth && this.width
        ? `0 0 ${this.width} ${this.model.height}`
        : '';

    return (
      <Host>
        {/*--------------- Title --------- */}
        {(this.model.title || this.model.subTitle) && (
          <uui-chart-title model={this.model}>
            {this.model.title?.useSlot && (
              <slot name="title" slot="title"></slot>
            )}
            {this.model.subTitle?.useSlot && (
              <slot name="sub-title" slot="sub-title"></slot>
            )}
          </uui-chart-title>
        )}

        <section class={chartSectionClass}>
          {/* ------- chart ---------- */}
          <svg
            class="chart__svg"
            {...(this.model.stretchToMaxWidth &&
              this.width && { viewBox: viewBoxCoords })}
            preserveAspectRatio="none"
          ></svg>

          {/* --------- legend ------------- */}
          {this.model.legend &&
            this.model.legend.showLegend &&
            this.getChartLegend()}
        </section>

        {/* ------------ Tooltip -------------- */}
        {this.model.tooltip && (
          <div
            class="chart__tooltip--container chart__tooltip--container-pointer-left"
            ref={el => (this.toolTipElement = el)}
          >
            {this.highlightedGroup != '' && this.getChartTooltip()}
          </div>
        )}

        {/* -----------  No Benchmark Tooltip -------------------*/}
        {this.model.enableCredibilityTooltip && (
          <div
            class="chart__tooltip--container chart__tooltip--container-pointer-bottom"
            ref={el => (this.noBenchmarkToolTipElement = el)}
          >
            {this.getNoBenchmarkTooltip()}
          </div>
        )}

        {!this.model.isShowChart && (
          <uui-chart-empty-message
            model={this.model}
            hasSlot={this.hasEmptyMessageSlot}
          >
            <slot name="empty-message" slot="empty-message"></slot>
          </uui-chart-empty-message>
        )}
      </Host>
    );
  }
}
